/**
 * Super-Admin only: /admin/security-audit
 * Zeigt alle 401/403-Vorfälle systemweit.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Shield, Loader2, RefreshCw, AlertTriangle, Filter, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { useReloadBus } from "@/lib/reloadBus";
import { AppHeader } from "@/components/AppHeader";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";

const SEVERITY_COLORS = {
  high:   { bg: "bg-red-500/10", text: "text-red-300", border: "border-red-500/40" },
  medium: { bg: "bg-amber-500/10", text: "text-amber-300", border: "border-amber-500/40" },
  low:    { bg: "bg-zinc-700/30", text: "text-zinc-400", border: "border-zinc-700" },
};

export default function AdminSecurityAudit() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ severity: "", status_code: "" });

  // Guard: only super-admin
  useEffect(() => {
    if (user && !user.is_super_admin) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (filter.severity) params.set("severity", filter.severity);
      if (filter.status_code) params.set("status_code", filter.status_code);
      const [{ data: ev }, { data: st }] = await Promise.all([
        api.get(`/superadmin/security-events?${params}`),
        api.get("/superadmin/security-events/stats"),
      ]);
      setEvents(ev || []); setStats(st);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Laden fehlgeschlagen");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter.severity, filter.status_code]); // eslint-disable-line
  // Iter88zbu — Silent-Reload nach jeder Mutation.
  useReloadBus("*", () => load(true));

  const purge = async () => {
    if (!window.confirm("Alle Security-Events älter als 90 Tage unwiderruflich löschen?")) return;
    try {
      const { data } = await api.delete("/superadmin/security-events?older_than_days=90");
      toast.success(`${data.deleted} Events gelöscht`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen"); }
  };

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="admin-security-audit">
      <AppHeader title="Security-Audit-Log" back="/admin" />
      <main className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <div>
            <h1 className="font-display text-xl sm:text-2xl font-bold flex items-center gap-2">
              <Shield className="text-red-400" size={22} /> Security-Audit-Log
            </h1>
            <p className="text-[12px] text-zinc-300 font-medium">Alle 401/403-Vorfälle systemweit (nur für Super-Admin sichtbar)</p>
          </div>
          <div className="flex gap-2">
            <button onClick={load} data-testid="audit-refresh" className="h-9 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-[12px] inline-flex items-center gap-1.5 font-medium">
              <RefreshCw size={14} /> Neu laden
            </button>
            <button onClick={purge} data-testid="audit-purge" className="h-9 px-3 rounded-lg bg-zinc-800 hover:bg-red-500/20 text-zinc-400 hover:text-red-300 text-[12px] inline-flex items-center gap-1.5 font-medium">
              <Trash2 size={14} /> Älter 90 Tage löschen
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4" data-testid="audit-stats">
            <StatCard label="Gesamt" value={stats.total} color="zinc" />
            <StatCard label="High (24h)" value={stats.last_24h?.high || 0} color="red" />
            <StatCard label="Medium (24h)" value={stats.last_24h?.medium || 0} color="amber" />
            <StatCard label="Low (24h)" value={stats.last_24h?.low || 0} color="zinc" />
          </div>
        )}

        {/* Top offenders */}
        {stats?.top_offenders_7d?.length > 0 && (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-3 mb-4">
            <div className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold font-bold mb-2">Top Auffällige (letzte 7 Tage)</div>
            <div className="flex flex-wrap gap-1.5">
              {stats.top_offenders_7d.map((o, i) => (
                <div key={i} className="px-2 py-1 rounded bg-[#09090B] border border-zinc-800 text-[12px]">
                  <span className="text-zinc-300">{o.tenant_name || "?"}</span>
                  <span className="text-zinc-500"> · {o.role || "?"}</span>
                  <span className="text-red-300 font-bold ml-1.5">{o.count}×</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-3 items-center">
          <Filter size={12} className="text-zinc-500" />
          <select value={filter.severity} onChange={(e) => setFilter({ ...filter, severity: e.target.value })} data-testid="audit-filter-severity" className="h-8 bg-[#09090B] border border-zinc-700 rounded px-2 text-[12px] text-zinc-300">
            <option value="">Alle Schweregrade</option>
            <option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
          </select>
          <select value={filter.status_code} onChange={(e) => setFilter({ ...filter, status_code: e.target.value })} data-testid="audit-filter-status" className="h-8 bg-[#09090B] border border-zinc-700 rounded px-2 text-[12px] text-zinc-300">
            <option value="">Alle Status-Codes</option>
            <option value="401">401 Unauthorized</option>
            <option value="403">403 Forbidden</option>
          </select>
        </div>

        {/* Events Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="animate-spin text-zinc-500" size={28} /></div>
        ) : events.length === 0 ? (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-12 text-center text-zinc-300 text-sm font-medium" data-testid="audit-empty">
            Keine Events vorhanden. Schön ruhig hier. 🛡️
          </div>
        ) : (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl overflow-hidden" data-testid="audit-events">
            <table className="w-full text-[12px] font-medium">
              <thead className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold bg-[#09090B]">
                <tr>
                  <th className="text-left py-2 px-3">Zeitpunkt</th>
                  <th className="text-left py-2 px-3">Sev</th>
                  <th className="text-left py-2 px-3">User</th>
                  <th className="text-left py-2 px-3">Mandant</th>
                  <th className="text-left py-2 px-3">Endpoint</th>
                  <th className="text-left py-2 px-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) => {
                  const sev = SEVERITY_COLORS[e.severity] || SEVERITY_COLORS.low;
                  return (
                    <tr key={e.id} className="border-t border-zinc-900 hover:bg-zinc-900/30">
                      <td className="py-1.5 px-3 font-mono text-[10.5px] text-zinc-400 whitespace-nowrap">{e.timestamp?.slice(0, 19).replace("T", " ")}</td>
                      <td className="py-1.5 px-3">
                        <span className={`px-1.5 py-0.5 rounded text-[9.5px] uppercase font-bold ${sev.bg} ${sev.text} border ${sev.border}`}>{e.severity}</span>
                      </td>
                      <td className="py-1.5 px-3">
                        <div className="text-zinc-200">{e.user_email || "—"}</div>
                        <div className="text-[12px] text-zinc-300">{e.user_role || "anonym"}</div>
                      </td>
                      <td className="py-1.5 px-3 text-zinc-300">{e.tenant_name || "—"}</td>
                      <td className="py-1.5 px-3">
                        <div className="font-mono text-[10.5px] text-zinc-300">{e.attempted_method} {e.attempted_path}</div>
                        {e.attempted_tenant_id && <div className="text-[12px] text-amber-300">→ Tenant: {e.attempted_tenant_id.slice(0, 8)}…</div>}
                      </td>
                      <td className="py-1.5 px-3">
                        <span className={`font-mono font-bold ${e.status_code === 403 ? "text-red-400" : "text-amber-400"}`}>{e.status_code}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}

const StatCard = ({ label, value, color }) => {
  const colorMap = {
    red: "text-red-300", amber: "text-amber-300", zinc: "text-zinc-200",
  };
  return (
    <div className="bg-[#18181B] border border-zinc-800 rounded-xl p-3">
      <div className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold font-bold">{label}</div>
      <div className={`text-2xl font-bold font-mono mt-1 ${colorMap[color] || "text-zinc-200"}`}>{value}</div>
    </div>
  );
};
